import { MAX_VALUE_16BITS } from 'jszip/lib/utils';
import { userRoles } from './rolesManager';

var axios = require('axios');
var GeneralClasses = require('@model/GeneralClasses');
const daysjs = require('../utils/dayjsConfig');

const SCIROOT_DISTRIBUTION_PATH = 'distribution';

// Structural spine: every accessible distribution and its farming units with
// id/name/role only (no fields, no sensors, no meteorology). Cheap "every load"
// call — the selected distribution's full tree is fetched via getDistributionById.
export var getDistributionsSkeleton = async () => {
  const res = await axios.get(`${SCIROOT_DISTRIBUTION_PATH}/skeleton`);
  // Router returns the bare array of distributions.
  return res.data;
};

// A distribution is "full" when every farming unit carries a (possibly empty)
// fields map — i.e. it came from getDistributionById, not the skeleton. Selecting
// to field level or reading fields is only safe on a full tree.
// Hydration is checked two ways: the server's explicit is_skeleton marker, and
// the structural signal that skeleton farming units carry no `fields` key. The
// explicit flag means this stays correct even if the skeleton payload grows.
export var isFullDistributionTree = distribution =>
  !!distribution &&
  distribution.is_skeleton !== true &&
  !!distribution.farming_units &&
  Object.values(distribution.farming_units).every(
    fu => fu && fu.fields !== undefined
  );

export var getDistributionById = async (distributionId, options = {}) => {
  const res = await axios.get(
    `${SCIROOT_DISTRIBUTION_PATH}/${distributionId}`,
    options.signal ? { signal: options.signal } : undefined
  );
  // The endpoint wraps the tree as { distribution: {...} }. Fall back to the
  // raw body so both response shapes are tolerated.
  const distribution = res.data?.distribution ?? res.data;
  // Distributions expects a raw { id: distribution } map, not a { distributions }
  // wrapper — otherwise the tree is dropped.
  const parsed = new GeneralClasses.Distributions({
    [distributionId]: distribution,
  });
  return parsed.distributions[distributionId];
};

// Performance debug flag - set to true to enable timing logs
const PERF_DEBUG = false;
const perfLog = msg => PERF_DEBUG && console.log(msg);

// Note: Avoid using module-level accumulation to prevent cross-call interference

export var is_admin = (all_distributions, distribution_id, farming_unit_id) => {
  if (
    all_distributions[distribution_id].role.toLowerCase() === userRoles.ADMIN
  ) {
    return true;
  } else if (
    farming_unit_id &&
    all_distributions[distribution_id]['farming_units'][farming_unit_id] &&
    all_distributions[distribution_id]['farming_units'][farming_unit_id]
      .role === userRoles.ADMIN
  ) {
    return true;
  } else {
    return false;
  }
};

export var getFirstInstallationDate = distributions => {
  let lastDate = daysjs().unix();
  for (const distributionKey in distributions) {
    const distribution = distributions[distributionKey];

    if (!distribution?.farming_units) continue;

    for (const farmingUnitKey in distribution.farming_units) {
      const farmingUnit = distribution.farming_units[farmingUnitKey];

      // Skeleton farming units omit `fields` until the full tree loads
      // (same guard as getFieldCrops — this runs on every load_distributions).
      if (!farmingUnit?.fields) continue;

      for (const fieldKey in farmingUnit.fields) {
        const field = farmingUnit.fields[fieldKey];

        if (parseInt(field.contract_data.installation_date) < lastDate) {
          lastDate = parseInt(field.contract_data.installation_date);
        }
      }
    }
  }

  return daysjs.unix(lastDate);
};

export var getFieldCrops = distributions => {
  let allCrops = [];

  if (!distributions || typeof distributions !== 'object') {
    return allCrops;
  }

  for (const distributionKey in distributions) {
    const distribution = distributions[distributionKey];

    if (!distribution?.farming_units) continue;

    for (const farmingUnitKey in distribution.farming_units) {
      const farmingUnit = distribution.farming_units[farmingUnitKey];

      if (!farmingUnit?.fields) continue;

      for (const fieldKey in farmingUnit.fields) {
        const field = farmingUnit.fields[fieldKey];

        // Safely access crop_type and ensure it's a valid string
        if (
          field?.crop_data?.crop_type &&
          typeof field.crop_data.crop_type === 'string'
        ) {
          allCrops.push(field.crop_data.crop_type.toLowerCase());
        }
      }
    }
  }

  let uniqueValues = [...new Set(allCrops)];
  return uniqueValues;
};

// Legacy paginated batch route (GET /distribution?pageIndex=N).
//
// It is no longer on the app's startup path — skeleton-first replaced that, and
// the per-boot load is now the skeleton plus the selected tree. The route is
// still served for two reasons:
//   1. older mobile builds in the wild still call it, and they expect
//      meteorological_sources on fields (hence includeStations defaults to true);
//   2. the internal screens that genuinely need EVERY distribution's fields
//      (technician assistant, transmitter test, remote configurations) bulk-load
//      through it — see ensure_all_distributions_loaded in distributionActions.
//      Those screens (and post-paint cache backfill) pass includeStations: false
//      and parallelPages: 5, which skips the met-stations table scan they would
//      never need on the wire and leaves one client connection-pool slot free
//      for selected by-id / history while bulk pages run.
//
// For loading one distribution, use getDistributionById instead; this route
// always returns whole pages. Startup cache backfill and cross-dist screens
// call it as distributions(1, 5, false) via ensure_all_distributions_loaded.
export var distributions = async (
  page = 1,
  parallelPages = 3,
  includeStations = true
) => {
  const totalStart = Date.now();
  let accumulatedDistributions = null;
  let totalNetworkTime = 0;
  let totalParseTime = 0;

  const isNonEmpty = data => !!(data && Object.keys(data).length > 0);

  const fetchSinglePage = async pageIndex => {
    const networkStart = Date.now();
    const res = await axios.get(SCIROOT_DISTRIBUTION_PATH, {
      params: { pageIndex, includeStations },
    });
    const networkTime = Date.now() - networkStart;
    perfLog(`[PERF] Distribution page ${pageIndex} network: ${networkTime}ms`);
    return { pageIndex, data: res.data, networkTime };
  };

  let currentPage = page;
  let reachedEnd = false;

  try {
    while (!reachedEnd) {
      const pagesToFetch = Array.from(
        { length: parallelPages },
        (_, idx) => currentPage + idx
      );

      const batchStart = Date.now();
      const results = await Promise.all(
        pagesToFetch.map(p => fetchSinglePage(p))
      );
      const batchNetworkTime = Date.now() - batchStart;
      perfLog(
        `[PERF] Distribution batch (pages ${pagesToFetch.join(',')}) total: ${batchNetworkTime}ms`
      );
      totalNetworkTime += batchNetworkTime;

      for (const { pageIndex, data } of results) {
        if (!isNonEmpty(data)) {
          reachedEnd = true;
          continue;
        }

        const parseStart = Date.now();
        const currentData = new GeneralClasses.Distributions(data);
        const parseTime = Date.now() - parseStart;
        perfLog(
          `[PERF] Distribution page ${pageIndex} parse (GeneralClasses.Distributions): ${parseTime}ms`
        );
        totalParseTime += parseTime;

        if (!accumulatedDistributions) {
          accumulatedDistributions = currentData;
        } else if (currentData && currentData.distributions) {
          Object.assign(
            accumulatedDistributions.distributions,
            currentData.distributions
          );
        }
      }

      if (!reachedEnd) {
        currentPage += parallelPages;
      }
    }

    const totalTime = Date.now() - totalStart;
    perfLog(
      `[PERF] Distribution load SUMMARY: total=${totalTime}ms, network=${totalNetworkTime}ms, parse=${totalParseTime}ms`
    );

    return accumulatedDistributions;
  } catch (e) {
    console.log(e.message);
    throw e;
  }
};

export var create_distribution = distribution_name => {
  return new Promise(function (resolve, reject) {
    axios
      .post(SCIROOT_DISTRIBUTION_PATH, {
        distribution_name: distribution_name,
      })
      .then(function (res) {
        resolve({
          distributionId: res.data['distribution_id'],
          distributionName: res.data['distribution_name'],
        });
      })
      .catch(function (e) {
        console.log(e.message);
        reject(e);
      });
  });
};

export var update_distribution = (distribution_id, distribution_name) => {
  return new Promise(function (resolve, reject) {
    axios
      .put(SCIROOT_DISTRIBUTION_PATH + '/' + distribution_id, {
        distribution_name: distribution_name,
      })
      .then(function (res) {
        resolve('');
      })
      .catch(function (e) {
        console.log(e.message);
        reject(e);
      });
  });
};

export var delete_distribution = distribution_id => {
  return new Promise(function (resolve, reject) {
    axios
      .delete(SCIROOT_DISTRIBUTION_PATH + '/' + distribution_id, {})
      .then(function (res) {
        resolve('');
      })
      .catch(function (e) {
        console.log(e.message);
        reject(e);
      });
  });
};

export const getSearchDataByFieldId = (fieldId, distributions) => {
  if (!fieldId || !distributions) {
    return null;
  }
  let selectedField;

  for (const distributionKey in distributions.distributions) {
    const distribution = distributions.distributions[distributionKey];

    for (const farmingUnitKey in distribution.farming_units) {
      const farmingUnit = distribution.farming_units[farmingUnitKey];

      for (const fieldKey in farmingUnit.fields) {
        const field = farmingUnit.fields[fieldKey];

        if (fieldKey == fieldId) {
          return { field, farmingUnit, distribution };
        }
      }
    }
  }

  return selectedField;
};
